'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { ScriptureReader } from './ScriptureReader';
import { createClient } from '@supabase/supabase-js';

interface ScriptureBook {
    id: string;
    title: string;
    description: string;
    slug: string;
    category: string;
}

interface ScriptureChapter {
    id: string;
    scripture_id: string;
    title: string;
    preface: string | null;
    position: number;
    content_text?: string; 
}

export const BibleReaderContainer = () => {
    const [books, setBooks] = useState<ScriptureBook[]>([]);
    const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
    const [chapters, setChapters] = useState<ScriptureChapter[]>([]);
    const [loading, setLoading] = useState(true);

    const supabase = createClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_KEY!
    );



    // Fetch all books on mount
    useEffect(() => {
        const fetchBooks = async () => {
            // Inside fetchBooks function
            const { data, error } = await supabase
                .from('scripture')
                .select('id, title, description, slug, category, content_type') 
                .in('category', ['Bible'])
                .in('language', ['english', 'both'])
                .order('created_at', { ascending: true });

            if (data && data.length > 0) {
                setBooks(data);
                // Start with Genesis (first book in the list)
                setSelectedBookId(data[0].id);
            }
            setLoading(false);
        };
        fetchBooks();
    }, []);

    // Fetch chapters when selectedBookId changes
    useEffect(() => {
        if (!selectedBookId) return;

        const fetchChapters = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('scripture_chapters')
                .select('*')
                .eq('scripture_id', selectedBookId)
                .order('position', { ascending: true });

            if (data) {
                setChapters(data);
            }
            setLoading(false);
        };

        fetchChapters();
    }, [selectedBookId]);

    const selectedBook = useMemo(() =>
        books.find(b => b.id === selectedBookId),
        [books, selectedBookId]);

    if (books.length === 0 && !loading) return <div>No books found.</div>;


    // Helper function to insert breaks every 4 sentences
    const formatScriptureText = (text: string) => {
        if (!text) return '';
        // Split by period followed by space (handles most sentences)
        const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];

        let formatted = '';
        for (let i = 0; i < sentences.length; i++) {
            formatted += sentences[i] + ' ';
            // Every 4 sentences, add two new lines
            if ((i + 1) % 4 === 0) {
                formatted += '\n\n';
            }
        }
        return formatted.trim();
    };

    // Inside the chapters.map logic:
    const newFormattedSections = chapters.map((ch, idx) => {
        const needsFormatting = selectedBook?.content_type === 'First Testament' || selectedBook?.category === 'Second Testament';
        return {
            id: ch.id,
            postId: ch.scripture_id,
            content: needsFormatting ? formatScriptureText(ch.preface || '') : (ch.preface || ''),
            position: ch.position,
            title: ch.title && ch.title.trim() !== '' ? ch.title : `Chapter ${ch.position || idx + 1}`,
            isPublic: true
        };
    });

    // Map scripture_chapters to the format expected by the BookReadNew component
    const formattedSections = chapters.map(ch => ({
        id: ch.id,
        postId: ch.scripture_id,
        content: ch.preface || '', 
        position: ch.position,
        title: ch.title,
        isPublic: true
    }));

    return (
        <ScriptureReader
            sections={newFormattedSections}
            description={selectedBook?.description}
            books={books}
            selectedBookId={selectedBookId}
            onBookChange={setSelectedBookId}
            loading={loading}
            onClose={() => { }}
        />
    );
};